import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Typography, Button, Space, Modal } from 'antd'
import {
  CalendarOutlined,
  VideoCameraOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  MessageOutlined,
  MedicineBoxOutlined,
  DollarOutlined,
  PhoneOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons'
import { handleClinicBooking, handleVirtualConsult } from '../../Bookings/utils/lib'
import { useDispatch, useSelector } from 'react-redux'
import { QRCodeSVG } from 'qrcode.react'

const { Title, Text, Paragraph } = Typography

const QRCodeModal = ({ visible, onCancel, qrCodeValue }) => (
  <Modal visible={visible} onCancel={onCancel} footer={null} centered width={300}>
    <div style={{ textAlign: 'center', padding: '24px' }}>
      <Title level={4}>Scan to Download</Title>
      <QRCodeSVG value={qrCodeValue} size={200} />
      <Paragraph style={{ marginTop: '16px' }}>Point your camera at the QR code to download the app.</Paragraph>
    </div>
  </Modal>
)

const NextAppointmentCard = () => (
  <Card
    title='Next Appointment'
    bordered={false}
    style={{
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      borderRadius: '12px',
      height: '100%'
    }}
    headStyle={{
      background: 'linear-gradient(135deg, #389e0d 0%, #52c41a 100%)',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      fontWeight: '600'
    }}
  >
    <Title level={5}>Dr. Dev Schoeman</Title>
    <Text type='secondary'>Home Visit</Text>
    <Paragraph style={{ margin: '16px 0' }}>
      <CalendarOutlined style={{ marginRight: '8px' }} />
      04 Aug 2025, 12:15
    </Paragraph>
    <Space>
      <Button type='primary'>View Details</Button>
      <Button>Reschedule</Button>
    </Space>
  </Card>
)

const QuickActionsCard = () => {
  const dispatch = useDispatch()
  const entityState = useSelector((state) => state.core.entity)

  return (
    <Card
      bordered={false}
      style={{
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        borderRadius: '12px',
        textAlign: 'center',
        height: '100%'
      }}
    >
      <Title level={4} style={{ marginBottom: '24px' }}>
        Quick Actions
      </Title>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Button
          type='primary'
          icon={<CalendarOutlined />}
          size='large'
          block
          onClick={() => handleClinicBooking(false, dispatch)}
        >
          Schedule Clinic Visit
        </Button>
        {entityState.virtualConsultations.enabled && (
          <Button
            type='primary'
            icon={<VideoCameraOutlined />}
            size='large'
            block
            onClick={() => handleVirtualConsult(false, dispatch)}
          >
            Schedule Virtual Consult
          </Button>
        )}
      </Space>
    </Card>
  )
}

const RecentActivityCard = () => (
  <Card
    title='Recent Activity & Messages'
    bordered={false}
    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}
    headStyle={{
      background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      fontWeight: '600'
    }}
  >
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <FileTextOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
        <div>
          <Text strong>New lab result available</Text>
          <br />
          <Text type='secondary' style={{ fontSize: '13px' }}>
            Full Blood Count
          </Text>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <MedicineBoxOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
        <div>
          <Text strong>Your prescription has been updated</Text>
          <br />
          <Text type='secondary' style={{ fontSize: '13px' }}>
            2 hours ago
          </Text>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <MessageOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />
        <div>
          <Text strong>Unread message from Dr. Smith</Text>
          <br />
          <Text type='secondary' style={{ fontSize: '13px' }}>
            Yesterday
          </Text>
        </div>
      </div>
    </Space>
  </Card>
)

const DownloadAppCard = () => {
  const [modalVisible, setModalVisible] = useState(false)
  const [qrCodeValue, setQrCodeValue] = useState('')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 575)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const showModal = (url) => {
    setQrCodeValue(url)
    setModalVisible(true)
  }

  const handleCancel = () => {
    setModalVisible(false)
    setQrCodeValue('')
  }

  return (
    <>
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #91d5ff 0%, #bae7ff 100%)',
          color: '#1f1f1f',
          borderRadius: '12px',
          textAlign: 'center',
          padding: '24px'
        }}
      >
        <Title level={3} style={{ color: '#1f1f1f', margin: '0 0 16px 0' }}>
          Download Our Mobile App
        </Title>
        <Paragraph style={{ color: '#595959', marginBottom: '20px' }}>
          For the best experience, including live chat, health tracking, and more.
        </Paragraph>
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
          {isMobile ? (
            // Mobile - Shiny circular buttons
            <>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: `
                    linear-gradient(145deg, #2a2a2a 0%, #000000 50%, #1a1a1a 100%),
                    linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)
                  `,
                  boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => showModal('https://apps.apple.com')}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.3),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 6px 12px rgba(0,0,0,0.4),
                    0 0 0 1px rgba(255,255,255,0.2)
                  `
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `
                }}
              >
                <FontAwesomeIcon
                  icon={faApple}
                  style={{
                    color: 'white',
                    fontSize: '20px',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                  }}
                />
              </div>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: `
                    linear-gradient(145deg, #2a2a2a 0%, #000000 50%, #1a1a1a 100%),
                    linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)
                  `,
                  boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => showModal('https://play.google.com')}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.3),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 6px 12px rgba(0,0,0,0.4),
                    0 0 0 1px rgba(255,255,255,0.2)
                  `
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `
                }}
              >
                <FontAwesomeIcon
                  icon={faGooglePlay}
                  style={{
                    color: 'white',
                    fontSize: '20px',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                  }}
                />
              </div>
            </>
          ) : (
            // Desktop/Tablet - Shiny full buttons
            <>
              <div
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: `
                    linear-gradient(145deg, #2a2a2a 0%, #000000 50%, #1a1a1a 100%),
                    linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)
                  `,
                  boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  fontWeight: '600',
                  fontSize: '14px',
                  gap: '8px',
                  color: 'white',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}
                onClick={() => showModal('https://apps.apple.com')}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.3),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 6px 16px rgba(0,0,0,0.4),
                    0 0 0 1px rgba(255,255,255,0.2)
                  `
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `
                }}
              >
                <FontAwesomeIcon
                  icon={faApple}
                  style={{
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                  }}
                />
                App Store
              </div>
              <div
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: `
                    linear-gradient(145deg, #2a2a2a 0%, #000000 50%, #1a1a1a 100%),
                    linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)
                  `,
                  boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  fontWeight: '600',
                  fontSize: '14px',
                  gap: '8px',
                  color: 'white',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}
                onClick={() => showModal('https://play.google.com')}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.3),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 6px 16px rgba(0,0,0,0.4),
                    0 0 0 1px rgba(255,255,255,0.2)
                  `
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = `
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    inset 0 -1px 0 rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 0 0 1px rgba(255,255,255,0.1)
                  `
                }}
              >
                <FontAwesomeIcon
                  icon={faGooglePlay}
                  style={{
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                  }}
                />
                Google Play
              </div>
            </>
          )}
        </div>
      </Card>
      <QRCodeModal visible={modalVisible} onCancel={handleCancel} qrCodeValue={qrCodeValue} />
    </>
  )
}

const ProfileHistoryCard = () => (
  <Card
    title='My Profile & History'
    bordered={false}
    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}
    headStyle={{
      background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      fontWeight: '600'
    }}
  >
    <Paragraph type='secondary' style={{ marginBottom: '20px' }}>
      View and manage your personal details, medical history, and dependents.
    </Paragraph>
    <Button type='primary' ghost icon={<ArrowRightOutlined />} block>
      View Profile & History
    </Button>
  </Card>
)

const QuickLinksCard = () => (
  <Card
    title='Quick Links'
    bordered={false}
    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}
    headStyle={{
      background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      fontWeight: '600'
    }}
  >
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Button type='text' style={{ justifyContent: 'flex-start', padding: '8px 0', height: 'auto' }}>
        <DollarOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
        Billing & Invoices
      </Button>
      <Button type='text' style={{ justifyContent: 'flex-start', padding: '8px 0', height: 'auto' }}>
        <MedicineBoxOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
        Manage Prescriptions
      </Button>
      <Button type='text' style={{ justifyContent: 'flex-start', padding: '8px 0', height: 'auto' }}>
        <CustomerServiceOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
        Contact Support
      </Button>
    </Space>
  </Card>
)

const PatientDesktop = () => {
  const dispatch = useDispatch()
  const entityState = useSelector((state) => state.core.entity)

  return (
    <div
      style={{
        padding: '24px 48px 48px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        minHeight: '100vh'
      }}
    >
      {/* Header with Greeting and Quick Actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Good afternoon, Josh
        </Title>
        <Space size='middle'>
          <Button
            type='primary'
            icon={<CalendarOutlined />}
            size='large'
            onClick={() => handleClinicBooking(false, dispatch)}
          >
            Schedule Clinic Visit
          </Button>
          {entityState.virtualConsultations.enabled && (
            <Button
              type='primary'
              icon={<VideoCameraOutlined />}
              size='large'
              onClick={() => handleVirtualConsult(false, dispatch)}
            >
              Schedule Virtual Consult
            </Button>
          )}
        </Space>
      </div>
      {/* Main Content Grid */}
      <Row gutter={[24, 24]}>
        {/* Top Row - Primary Cards */}
        <Col xs={24} md={12} lg={8}>
          <NextAppointmentCard />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <ProfileHistoryCard />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <DownloadAppCard />
        </Col>

        {/* Bottom Row - Secondary Cards */}
        <Col xs={24} lg={16}>
          <RecentActivityCard />
        </Col>
        <Col xs={24} lg={8}>
          <QuickLinksCard />
        </Col>
      </Row>
    </div>
  )
}

export default PatientDesktop
